import React, { createRef, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
	createSearchParams,
	Link,
	useLocation,
	useNavigate,
	useOutletContext,
	useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import {
	Alert,
	Button,
	Col,
	Container,
	Form,
	FormFeedback,
	FormGroup,
	InputGroup,
	InputGroupAddon,
	InputGroupText,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	Spinner,
	UncontrolledTooltip,
} from "reactstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { Divider } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useMutation } from "@tanstack/react-query";
import { useSessionStorageState } from "ahooks";
import { isAxiosError } from "axios";
import classNames from "classnames";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import * as yup from "yup";

import { LoginFormWithAccountType } from "@/components/LoginFormWithAccountType";
import CourseConsultantCode from "components/CourseConsultantCode";
import EmbeddedResetPasswordModal from "components/EmbeddedResetPasswordModal";
import OTPModalSignup from "components/Modals/OtpModalSignup";

import { login } from "@/actions/auth";
import { clearMessage } from "@/actions/message";
import { logout, switchAccountType } from "actions/auth";
import { REGISTER_SUCCESS } from "actions/types";
import { useAuth } from "hooks/selectors/useAuth";
import { useCheckout } from "hooks/selectors/useCheckout";
import {
	cacheRegform,
	fetchCheckoutData,
	saveCheckoutAnalyticsData,
	setRegistrationId,
} from "reducers/courseRegisterSlice";

import generalApi from "@/services/general-api";

import { getUrlParams } from "@/views/pages/checkout/CheckoutPage";
import CreateAccountFloating from "@/views/pages/checkout/components/CreateAccountFloating";
import { CheckoutLogoutButton } from "@/views/pages/checkout/components/LogoutButton";
import { to_official_website_url } from "@/utilities/constant";
import { GTMCheckoutUserLoginSuccess, logGTMUser } from "@/utilities/googleTagManager";
import {
	CONTACT_NO_CHECKOUT,
	COUNTRIES_OPTIONS,
	GENDER_OPTIONS,
	LOAN_OPTIONS,
	NATIONALITY_OPTIONS,
	QUALIFICATOIN_LEVEL_OPTION,
} from "utilities/constant";
import { getErrorMessage } from "utilities/error";
import {
	GTMCheckoutSingpassRetrieveInfoButton,
	GTMCheckoutSingpassRetrieveInfoFailed,
	GTMCheckoutSingpassRetrieveInfoSuccess,
	GTMCheckoutStep1Account,
	GTMCheckoutStep1AccountExit,
	GTMCheckoutStep1GoEndorse,
	GTMCheckoutSubmitRegform,
	GTMCheckoutSubmitRegformSuccess,
	GTMCheckoutUserForgetPasswordButton,
	GTMCheckoutUserForgetPasswordSuccess,
	GTMCheckoutUserLoginButton,
	GTMCheckoutUserLoginOtpVerify,
	GTMCheckoutUserLoginOtpVerifySuccess,
	GTMCheckoutUserRegisterButton,
	GTMCheckoutUserRegisterSuccess,
} from "utilities/googleTagManager";
import { yupHelper } from "utilities/yupHelpers";

import CheckoutLoginImage from "@/assets/img/Checkout/checkout_login.jpg";
import SingpassButton from "@/assets/img/Checkout/MyinfoButton/Neutral@4x.png";
import { ReactComponent as LockIcon } from "@/assets/img/Login/password.svg";
import { ReactComponent as UserIcon } from "@/assets/img/Login/user.svg";

import styles from "./checkoutregsiterpage.module.scss";
import SeniorKycConsentModal from "./components/SeniorKycConsentModal";
import SeniorReviewModal from "./components/SeniorReviewModal";

const API_URL = process.env.REACT_APP_API_URL;

// Workaround:
// cache the created account state , or when the sub page refreshing due to reloading
// the successfully create modal will still show
const createdAccountRef = createRef(false);

export const useSendKycApproval = () =>
	useMutation((payload) => generalApi.sendKycApproval(payload));

export function CheckoutRegisterPage() {
	// const [isUnderMaintenance, setIsUnderMaintenance] = useState(false);
	// const [isSingPassUnderMaintenance, setIsSingPassUnderMaintenance] = useState(false);
	// const [isSfcUnderMaintenance, setIsSfcUnderMaintenance] = useState(false);
	const dispatch = useDispatch();
	const { userData } = useAuth();
	const [isTryLogout, setIsTryLogout] = useState(false);
	const [ccCodeReg, setCcCodeReg] = useState("");
	const [showConsent, setShowConsent] = useState(false);
	const [showVideo, setShowVideo] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [videoConsentDone, setVideoConsentDone] = useState(false);
	// === Consent / Video Recording gating ===
	// keep a closure-friendly callback to resume original flow after video
	const resumeProceedRef = useRef(() => {});

	const schema = useMemo(() => {
		const baseSchema = {
			name: yupHelper.fullname(),
			nric: yupHelper.nric(),
			dob: yup.string().required(),
			nationality: yup.string().required(),
			gender: yup.string().required().nonNullable(),
			email: yup.string().email().required(),
			mobile: yupHelper.mobile(),
			education: yup.string().required(),
			designation: yup.string().required(),
			laptopLoan: yup.string().required().default("0"),
			postal_code: yup.string().notRequired(),
			street: yup.string().notRequired(),
			unit: yup.string().notRequired(),
			block: yup.string().notRequired(),
			floor: yup.string().notRequired(),
			building: yup.string().notRequired(),
			country: yup.string().notRequired(),
			fill_with_singpass: yup.bool().required().default(false),
		};

		// if user is not logged in, add password fields validation
		if (!userData) {
			baseSchema.password = yupHelper.password();
			baseSchema.password_confirmation = yup
				.string()
				.required("Please retype your password.")
				.oneOf([yup.ref("password")], "Your passwords do not match.");
		}

		return yup.object(baseSchema);
	}, [userData]);

	const {
		courserun_id,
		registrationform_id,
		registerFormCache,
		learners,
		registerForm,
		course,
		courserun,
	} = useCheckout();
	const [loginModal, setLoginModal] = useState(false);

	// TODO: STORE the state locally , otherwise when they refresh the page , the state will be gone
	const [errorData, setErrorData] = useState(null);
	const [editMode, setEditMode] = useState(true);
	const [isSingpass, setisSingpass] = useState(false);
	const [lockedField, setLockedField] = useState({});
	const [createAccountSuccessModal, setcreateAccountSuccessModal] = useState(
		createdAccountRef.current,
	);
	const [searchParams] = useSearchParams();

	const [showPassword, setShowPassword] = useState(false);
	const toggleSuccessCreateAccountModal = () =>
		setcreateAccountSuccessModal(!createAccountSuccessModal);
	const [otpmodal, setOTPModal] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const { openEnquiryModal = () => {}, isSingPassUnderMaintenance } = useOutletContext();
	const { checkoutAnalyticsData } = useCheckout();
	const [isFirstTime, setIsFirstTime] = useState(true);
	const toggleLoginModal = () => {
		setLoginModal(!loginModal);
	};
	const sendKyc = useSendKycApproval();
	const [kycSuccessOpen, setKycSuccessOpen] = useState(false);
	const [kycSuccessHtml, setKycSuccessHtml] = useState("");

	const runId =
		(learners && learners.courserun_id) ||
		(registerFormCache && registerFormCache.registrationform_courserun) ||
		"unknown";

	const [recordedVideoB64, setRecordedVideoB64] = useSessionStorageState(
		`checkout_recorded_video_${runId}`,
		"",
	);

	const recordedVideoRef = useRef("");

	const {
		register,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
		watch,
	} = useForm({ resolver: yupResolver(schema) });

	const wDob = watch("dob");
	const wEdu = watch("education");
	const wDesignation = watch("designation");
	const [seniorModalOpen, setSeniorModalOpen] = useState(false);
	const [seniorNeedsReview, setSeniorNeedsReview] = useState(false);
	const [showKycConsent, setShowKycConsent] = useState(false);
	const [showSeniorForm, setShowSeniorForm] = useState(false);
	const [seniorPrefill, setSeniorPrefill] = useState(null);

	const populateRegformByUser = (data) => {
		reset({
			name: data.user.learnermaster_name,
			nric: data.user.learnermaster_nric,
			dob: data.user.learnermaster_dob,
			nationality: data.user.learnermaster_nationality,
			email: data.user.learnermaster_email,
			mobile: data.user.learnermaster_mobile,
			education: data.user.learnermaster_qualificationlevel,
			gender: data.user.learnermaster_gender,
			designation: data.user.learnermaster_designation,
			street: data.user.learnermaster_address_street,
			building: data.user.learnermaster_address_building,
			block: data.user.learnermaster_address_block,
			floor: data.user.learnermaster_address_floor,
			unit: data.user.learnermaster_address_unit,
			postal_code: data.user.learnermaster_address_postalcode,
			country: data.user.learnermaster_address_country,
		});
	};

	const handleContinue = useMutation(
		(data) => {
			setErrorData(null);
			dispatch(
				cacheRegform({ ...data, recorded_video: data.recorded_video ?? recordedVideoB64 ?? "" }),
			);

			if (userData && userData.user) {
				if (userData.user.email_verified_at) {
					setOTPModal(false);
				} else {
					// open OTP modal here if user has not verified email
					GTMCheckoutUserLoginOtpVerify(checkoutAnalyticsData, userData);
					setOTPModal(true);
					// stop here
					return;
				}
			}

			/**
			 * if user is not logged in, create an account for them
			 * else submit the regform
			 */
			if (!userData) {
				// end here
				GTMCheckoutUserRegisterButton(checkoutAnalyticsData, userData);
				return generalApi
					.checkAge({
						learner_dob: data.dob,
						learner_mobile: data.mobile,
						// (Optionally pass learner_name & course_ref so backend can prefill)
						learner_name: data.name,
						courserun_id: courserun_id,
					})
					.then(async (responseData) => {
						if (responseData.status === "Success") {
							generalApi
								.createAccountForOnlineRegistration({
									name: data.name,
									email: data.email,
									nric: data.nric,
									dob: data.dob,
									nationality: data.nationality,
									mobile: data.mobile,
									qualificationlevel: data.education,
									designation: data.designation,
									gender: data.gender,
									password: data.password,
									password_confirmation: data.password_confirmation,
									address_postal: data.postal_code,
									address_street: data.street,
									address_block: data.block,
									address_floor: data.floor,
									address_unit: data.unit,
									address_building: data.building,
									address_country: data.country,
									fill_with_singpass: data.fill_with_singpass,
									recorded_video:
										data.recorded_video ?? recordedVideoRef.current ?? recordedVideoB64 ?? "",
								})
								.then((response) => {
									createdAccountRef.current = true;
									dispatch({
										type: REGISTER_SUCCESS,
										payload: { user: response.data },
									});
									GTMCheckoutUserRegisterSuccess(checkoutAnalyticsData, { user: response.data });
									GTMCheckoutUserLoginOtpVerify(checkoutAnalyticsData, { user: response.data });
									setOTPModal(true);
								})
								.catch((error) => {
									console.warn("Create Account Error", error);
									if (isAxiosError(error)) {
										const responseData = error.response.data;

										if (responseData.status === "Error") {
											if (responseData.error_code === "DUPLICATED_ID") {
												const errorMessage = Object.values(responseData.data).map((item) => {
													return <li key={item}>{item}</li>;
												});
												setErrorData({
													element: (
														<ErrorDisplay
															title="Create account Failed"
															description={<ul>{errorMessage}</ul>}>
															<p>
																<Button onClick={toggleLoginModal}>
																	Press here to login here if you have an existing account
																</Button>
															</p>
														</ErrorDisplay>
													),
												});
											} else {
												setErrorData({
													element: (
														<ErrorDisplay
															title="Create account Failed"
															description={getErrorMessage(error)}></ErrorDisplay>
													),
												});
											}
										} else {
											throw error;
										}
									} else {
										throw error;
									}
								});
						}
					})
					.catch((error) => {
						if (isAxiosError(error)) {
							const responseData = error.response?.data;
							if (!responseData) throw error;

							if (responseData.status === "NEEDS_SENIOR_REVIEW") {
								const pf = responseData.prefill || {};
								const coursesPrefill =
									Array.isArray(pf.courses) && pf.courses.length > 0
										? pf.courses.map((c) => Number(c) || c) // if backend already sends IDs, keep as-is; if strings, coerce
										: course?.course_id
										? [course.course_title]
										: [];

								setSeniorPrefill({
									name: pf.name ?? learners.learner_name ?? "",
									dob: pf.dob ?? learners.learner_dob ?? "",
									mobile: pf.mobile ?? learners.learner_mobile ?? "",
									courses: coursesPrefill,
								});

								setSeniorNeedsReview(true);
								setShowKycConsent(true); // 1) show consent first
								return;
							}

							if (responseData.status === "Error") {
								// ... your existing error modal
								return;
							}

							throw error;
						}
					});
			} else {
				GTMCheckoutSubmitRegform(checkoutAnalyticsData, userData);
				return generalApi
					.checkAge({
						learner_dob: data.dob,
						learner_mobile: data.mobile,
						// (Optionally pass learner_name & course_ref so backend can prefill)
						learner_name: data.name,
						courserun_id: courserun_id,
					})
					.then(async (responseData) => {
						if (responseData.status === "Success") {
							const cccToSend = (
								data?.course_consultant_code ??
								data?.ccc ??
								checkoutAnalyticsData?.CCC ??
								""
							).trim();

							generalApi
								.submitOnlineRegistration({
									registrationform_id: registrationform_id,
									registrationform_courserun: courserun_id,
									registrationform_sponsoredbycompany_yes_no: "No",
									learner_nric: data.nric,
									learner_name: data.name,
									learner_dob: data.dob,
									learner_nationality: data.nationality,
									learner_email: data.email,
									learner_mobile: data.mobile,
									learner_qualificationlevel: data.education,
									learner_designation: data.designation,
									learner_laptoploan: data.laptopLoan,
									learner_learnermaster_id: userData.user.learnermaster_id,
									learner_gender: data.gender,
									learner_postal: data.postal_code,
									learner_street: data.street,
									learner_block: data.block,
									learner_floor: data.floor,
									learner_unit: data.unit,
									learner_building: data.building,
									learner_country: data.country,
									fill_with_singpass: data.fill_with_singpass,
									referral: checkoutAnalyticsData.referral,
									// invite: checkoutAnalyticsData.invite,
									recorded_video:
										data.recorded_video ?? recordedVideoRef.current ?? recordedVideoB64 ?? "",
									ccc: cccToSend,
								})
								.then(async (responseData) => {
									GTMCheckoutSubmitRegformSuccess * (checkoutAnalyticsData, userData);
									const registrationform_id = responseData.data.registrationform_id;
									dispatch(setRegistrationId({ registrationform_id: registrationform_id }));

									try {
										const b64 = recordedVideoRef.current || recordedVideoB64 || "";
										if (b64) {
											await generalApi.saveMERSeniorVideo({
												registrationform_id,
												recorded_video: b64,
												from_senior_popup:
													videoCtx.variant === "SENIOR" || videoCtx.variant === "SENIOR_MER"
														? 1
														: 0,
											});
											// keep redux/session in sync
											dispatch(cacheRegform({ ...(registerFormCache || {}), recorded_video: b64 }));
										}
									} catch (e) {
										console.warn("saveMERSeniorVideo (post-submit) failed", e);
										// soft-fail only; do not block navigation
									}
									return dispatch(
										fetchCheckoutData({ registrationform_id: registrationform_id }),
									).then(() => {
										if (createdAccountRef.current) {
											setcreateAccountSuccessModal(true);
										} else {
											// toast.success("Proceed to payment", {
											// 	icon: "🎉",
											// });

											const newSearchParam = [];

											for (let entry of searchParams.entries()) {
												newSearchParam.push(entry);
											}
											if (newSearchParam.uuidv4) {
												delete newSearchParam.uuidv4;
											}

											normalizeCcInParams(newSearchParam, checkoutAnalyticsData?.CCC);

											// // Append 'cc' only if present (from state or form)
											// const cc = (checkoutAnalyticsData?.CCC ?? "").trim();
											// if (cc) newSearchParam.push(["cc", cc]);

											generalApi
												.checkGrant({
													learner_nric: data.nric,
													learner_dob: data.dob,
													learner_nationality: data.nationality,
													course_ref: course.course_ref,
													courserun_api_id: courserun.courserun_apicourserunid,
													courserun_course_from: courserun.courserun_course_from,
													trigger_from: "FALCOMM",
												})
												.then(async (responseData) => {
													if (responseData.status === "Success") {
														toast.success("Proceed to payment", {
															icon: "🎉",
														});

														window.scrollTo({ top: 0 });
														GTMCheckoutStep1GoEndorse(checkoutAnalyticsData, userData);
														navigate({
															pathname: "/checkout/course",
															search: createSearchParams(
																getUrlParams(newSearchParam, checkoutAnalyticsData, courserun_id),
															).toString(),
														});
													}
												})
												.catch((error) => {
													if (isAxiosError(error)) {
														console.warn("Check Grant Error", error);
														const responseData = error.response.data;

														if (responseData.status === "Error") {
															setErrorData({
																element: (
																	<Modal isOpen centered>
																		<ModalHeader>Registration Failed</ModalHeader>
																		<ModalBody>
																			<div
																				dangerouslySetInnerHTML={{
																					__html: responseData.error_msg,
																				}}></div>
																		</ModalBody>
																		<ModalFooter>
																			<Link to={to_official_website_url("/courses/")} replace>
																				<Button color="primary">Return to course</Button>
																			</Link>
																		</ModalFooter>
																	</Modal>
																),
															});
															return;
														} else {
															throw error;
														}
													}
												});

											// window.scrollTo({ top: 0 });
											// GTMCheckoutStep1GoEndorse(checkoutAnalyticsData, userData);
											// navigate({
											// 	pathname: "/checkout/course",
											// 	search: createSearchParams(getUrlParams(newSearchParam, checkoutAnalyticsData, courserun_id)).toString(),
											// });
										}
									});
								})
								.catch((error) => {
									if (isAxiosError(error)) {
										console.warn("Resubmit Registration Error", error);
										const responseData = error.response.data;

										if (responseData.status === "Error") {
											if (responseData.error_code === "HAS_REGISTERED_SAME_COURSE") {
												setErrorData({
													element: (
														<Modal isOpen centered>
															<ModalHeader>Registration Failed</ModalHeader>
															<ModalBody>{responseData.message}</ModalBody>
															<ModalFooter>
																<Link to={to_official_website_url("/courses/")} replace>
																	<Button color="primary">Return to course</Button>
																</Link>
															</ModalFooter>
														</Modal>
													),
												});
											} else if (responseData.error_code === "HAS_REGISTERED_SAME_DATE") {
												setErrorData({
													element: (
														<Modal isOpen centered>
															<ModalHeader>Registration Failed</ModalHeader>
															<ModalBody>{responseData.message}</ModalBody>
															<ModalFooter>
																<Link
																	to={to_official_website_url(responseData.data.fca_seo_url)}
																	replace>
																	<Button color="primary">Select another course date</Button>
																</Link>
															</ModalFooter>
														</Modal>
													),
												});
											} else if (responseData.error_code === "FULLY_BOOKED") {
												setErrorData({
													element: (
														<Modal isOpen centered>
															<ModalHeader>Fully booked</ModalHeader>
															<ModalBody>{responseData.message}</ModalBody>
															<ModalFooter>
																{/* <Link to={"/course/course-info/" + responseData.data.course_id} replace> */}
																<Link to={responseData.data.fca_seo_url} replace>
																	<Button color="primary">Select another course date</Button>
																</Link>
															</ModalFooter>
														</Modal>
													),
												});
											} else if (responseData.error_code === "HAS_TBA_NOSHOW_COURSE") {
												setErrorData({
													element: (
														<Modal isOpen centered>
															<ModalHeader>Pending Courses</ModalHeader>
															<ModalBody>{responseData.message}</ModalBody>
															<ModalFooter>
																<a href={to_official_website_url("/contact-us/")}>Contact Form</a>
															</ModalFooter>
														</Modal>
													),
												});
											} else {
												setErrorData({
													element: (
														<ErrorDisplay
															title="Registration Failed"
															description={
																<>
																	<div>{responseData.message}</div>
																	<div>{getErrorMessage(error)}</div>
																</>
															}></ErrorDisplay>
													),
												});
											}
											return;
										} else {
											throw error;
										}
									}
								});
						}
					})
					.catch((error) => {
						if (isAxiosError(error)) {
							const responseData = error.response?.data;
							if (!responseData) throw error;

							if (responseData.status === "NEEDS_SENIOR_REVIEW") {
								const pf = responseData.prefill || {};
								const coursesPrefill =
									Array.isArray(pf.courses) && pf.courses.length > 0
										? pf.courses
										: course?.course_ref
										? [course.course_ref]
										: [];

								setSeniorPrefill({
									name: pf.name ?? learners.learner_name ?? "",
									dob: pf.dob ?? learners.learner_dob ?? "",
									mobile: pf.mobile ?? learners.learner_mobile ?? "",
									courses: coursesPrefill,
								});

								setSeniorNeedsReview(true);
								setShowKycConsent(true); // 1) show consent first
								return;
							}

							if (responseData.status === "Error") {
								// ... your existing error modal
								return;
							}

							throw error;
						}
					});
			}
		},
		{
			onError: (error) => {
				console.warn("Checkout -> handleContinue -> Error", error);
				toast.error("Error : ", getErrorMessage(error));
			},
		},
	);

	const handleClickEdit = (e) => {
		e.preventDefault();
		setEditMode(true);
	};

	const SPECIAL_COURSE_IDS = [61];
	const LOWER_EDU_LIST = [
		"Below PSLE",
		"PSLE",
		"LOWER SECONDARY (SECONDARY EDUCATION WITHOUT O LEVEL/N LEVEL PASS)",
	];

	// Build once per render (use real courseId you have in scope)
	// const videoCtx = deriveVideoVariant({
	// 	dob: learners?.learner_dob,
	// 	designation: learners?.learner_designation,
	// 	qualification: learners?.learner_qualificationlevel,
	// 	courseId: course?.course_id || courserun?.learners_course || courserun?.course_id,
	// });

	const videoCtx = useMemo(() => {
		return deriveVideoVariant({
			dob: wDob || learners?.learner_dob,
			designation: wDesignation || learners?.learner_designation,
			qualification: wEdu || learners?.learner_qualificationlevel,
			courseId: course?.course_id || courserun?.learners_course || courserun?.course_id,
			runDate: courserun?.courserun_course_from,
		});
	}, [
		wDob,
		wEdu,
		wDesignation,
		learners?.learner_dob,
		learners?.learner_designation,
		learners?.learner_qualificationlevel,
		course?.course_id,
		courserun?.learners_course,
		courserun?.course_id,
	]);

	// tiny helper
	function hasRecording() {
		return !!(recordedVideoRef.current || recordedVideoB64);
	}

	function startRecapture() {
		// wipe the existing recording everywhere we manage
		recordedVideoRef.current = "";
		setRecordedVideoB64(""); // clears the sessionStorage value

		// reset the consent gate
		setVideoConsentDone(false);

		// route: Preview -> Consent -> Recorder
		setShowPreview(false);
		setShowConsent(true);

		dispatch(cacheRegform({ ...(registerFormCache || {}), recorded_video: "" }));
	}

	// Normalize 'cc' in a [key, value][] style array of query params
	function normalizeCcInParams(paramPairs, ccFromState) {
		// remove any pre-existing 'cc' or 'ccc'
		for (let i = paramPairs.length - 1; i >= 0; i--) {
			const k = paramPairs[i][0];
			if (k === "cc" || k === "ccc") paramPairs.splice(i, 1);
		}
		// add trimmed 'cc' if present
		const cc = (ccFromState || "").trim();
		if (cc) paramPairs.push(["cc", cc]);
		return paramPairs;
	}

	// Update ?cc=... in the URL (no page reload)
	function updateCcParamInUrl({ location, navigate, cc }) {
		const sp = new URLSearchParams(location.search);
		// clean up any legacy 'ccc'
		sp.delete("ccc");

		const trimmed = (cc || "").trim();
		if (trimmed) sp.set("cc", trimmed);
		else sp.delete("cc");

		navigate(
			{ pathname: location.pathname, search: sp.toString() },
			{ replace: true }, // don't add extra history entries
		);
	}

	// 1) helper: build params once, same as your original flow
	const buildSearchParams = () => {
		const newSearchParam = [];
		for (let entry of searchParams.entries()) newSearchParam.push(entry);
		if (newSearchParam.uuidv4) delete newSearchParam.uuidv4;
		normalizeCcInParams(newSearchParam, checkoutAnalyticsData?.CCC);
		return newSearchParam;
	};

	// 2) helper: run checkGrant and navigate
	const continueToGrantAndNavigate = async () => {
		const newSearchParam = buildSearchParams();

		const responseData = await generalApi.checkGrant({
			learner_nric: learners.learner_nric,
			learner_dob: learners.learner_dob,
			learner_nationality: learners.learner_nationality,
			courserun_api_id: courserun.courserun_apicourserunid,
			course_ref: course.course_ref,
			courserun_course_from: courserun.courserun_course_from,
			trigger_from: "FALCOMM",
		});

		if (responseData.status === "Success") {
			toast.success("Proceed to payment", { icon: "🎉" });
			window.scrollTo({ top: 0 });
			GTMCheckoutStep1GoEndorse(checkoutAnalyticsData, userData);
			navigate({
				pathname: "/checkout/course",
				search: createSearchParams(
					getUrlParams(newSearchParam, checkoutAnalyticsData, courserun_id),
				).toString(),
			});
		}
	};

	// 3) CORE: checkAge → (if OK) video consent gate → checkGrant
	async function proceedToPaymentCore() {
		try {
			// A) CHECK AGE FIRST
			const ageRes = await generalApi.checkAge({
				learner_dob: learners.learner_dob,
				learner_mobile: learners.learner_mobile,
				learner_name: learners.learner_name,
				courserun_id: courserun_id,
			});

			if (ageRes.status === "Success") {
				// B) VIDEO CONSENT GATE only after age is OK
				const learnerSnap = {
					learner_dob: learners?.learner_dob,
					learner_qualificationlevel: learners?.learner_qualificationlevel,
				};
				const ccc = checkoutAnalyticsData?.CCC;

				if (!videoConsentDone && needsVideoConsent(learnerSnap, ccc)) {
					// defer the grant + nav until after video confirmed/recaptured
					resumeProceedRef.current = async () => {
						setVideoConsentDone(true);
						await continueToGrantAndNavigate();
					};

					if (hasRecording()) {
						setShowPreview(true);
					} else {
						setShowConsent(true);
					}
					return; // stop here; flow resumes via resumeProceedRef after consent
				}

				// C) no video gating → continue straight to grant + navigate
				await continueToGrantAndNavigate();
				return;
			}
		} catch (error) {
			// D) handle checkAge errors (NEEDS_SENIOR_REVIEW / Error)
			if (isAxiosError(error)) {
				const responseData = error.response?.data;
				if (!responseData) throw error;

				if (responseData.status === "NEEDS_SENIOR_REVIEW") {
					const pf = responseData.prefill || {};
					const coursesPrefill =
						Array.isArray(pf.courses) && pf.courses.length > 0
							? pf.courses
							: course?.course_id
							? [course.course_title]
							: [];

					setSeniorPrefill({
						name: pf.name ?? learners.learner_name ?? "",
						dob: pf.dob ?? learners.learner_dob ?? "",
						mobile: pf.mobile ?? learners.learner_mobile ?? "",
						courses: coursesPrefill,
					});

					setSeniorNeedsReview(true);
					setShowKycConsent(true); // consent first; then your form modal
					return;
				}

				if (responseData.status === "Error") {
					setErrorData({
						element: (
							<Modal isOpen centered>
								<ModalHeader>Minimum Entry Requirements</ModalHeader>
								<ModalBody>
									<div dangerouslySetInnerHTML={{ __html: responseData.error_msg }} />
								</ModalBody>
								<ModalFooter>
									<Link to={to_official_website_url("/courses/")} replace>
										<Button color="primary">Return to course</Button>
									</Link>
								</ModalFooter>
							</Modal>
						),
					});
					return;
				}
			}
			throw error;
		}
	}

	// 4) BUTTON HANDLER: no duplicate calls anymore — just call the core
	const handleProceedToPaymentPage = (e) => {
		e.preventDefault();
		// previously you ran video consent gate here; now the core handles it after age passes
		proceedToPaymentCore();
	};

	// Gate the Continue submit (Create Account / Continue) with CC + (Age>=67 or Edu Primary/Below)
	const handleSubmitContinue = async (formData) => {
		const latestCCC = (
			formData?.course_consultant_code ??
			formData?.ccc ??
			checkoutAnalyticsData?.CCC ??
			""
		).trim();

		updateCcParamInUrl({ location, navigate, cc: latestCCC });

		const learnerSnap = {
			learner_dob: formData?.dob,
			learner_qualificationlevel: formData?.education,
		};
		const ccc = (checkoutAnalyticsData?.CCC || "").trim();

		if (!ccc && checkoutAnalyticsData?.CCC) {
			dispatch(saveCheckoutAnalyticsData({ ...checkoutAnalyticsData, CCC: "" }));
		}

		try {
			// ✅ 1. CHECK AGE FIRST
			const ageRes = await generalApi.checkAge({
				learner_dob: formData?.dob,
				learner_mobile: formData?.mobile,
				learner_name: formData?.name,
				courserun_id: courserun_id,
			});

			if (ageRes.status === "Success") {
				// ✅ 2. THEN VIDEO CONSENT IF NEEDED
				if (!videoConsentDone && needsVideoConsent(learnerSnap, ccc)) {
					resumeProceedRef.current = (b64) => {
						setVideoConsentDone(true);
						handleContinue.mutate({
							...formData,
							recorded_video: b64 || recordedVideoRef.current || recordedVideoB64 || "",
						});
					};
					if (hasRecording()) setShowPreview(true);
					else setShowConsent(true);
					return;
				}

				// ✅ 3. Otherwise proceed normally
				handleContinue.mutate({
					...formData,
					recorded_video: recordedVideoRef.current || recordedVideoB64 || "",
				});
				return;
			}
		} catch (error) {
			// keep your same senior review / error handling here
			if (isAxiosError(error)) {
				const responseData = error.response?.data;
				if (!responseData) throw error;

				if (responseData.status === "NEEDS_SENIOR_REVIEW") {
					const pf = responseData.prefill || {};
					const safeLearner = learners || {};

					const coursesPrefill =
						Array.isArray(pf.courses) && pf.courses.length > 0
							? pf.courses
							: course?.course_id
							? [course.course_title]
							: [];
					setSeniorPrefill({
						name: pf.name ?? safeLearner.learner_name ?? formData?.name ?? "",
						dob: pf.dob ?? safeLearner.learner_dob ?? formData?.dob ?? "",
						mobile: pf.mobile ?? safeLearner.learner_mobile ?? formData?.mobile ?? "",
						courses: coursesPrefill,
					});
					setSeniorNeedsReview(true);
					setShowKycConsent(true);
					return;
				}
				if (responseData.status === "Error") {
					setErrorData({
						element: (
							<Modal isOpen centered>
								<ModalHeader>Minimum Entry Requirements</ModalHeader>
								<ModalBody>
									<div dangerouslySetInnerHTML={{ __html: responseData.error_msg }} />
								</ModalBody>
								<ModalFooter>
									<Link to={to_official_website_url("/courses/")} replace>
										<Button color="primary">Return to course</Button>
									</Link>
								</ModalFooter>
							</Modal>
						),
					});
					return;
				}
			}
			throw error;
		}
	};

	const handleOTPSuccess = () => {
		toast.success("OTP Verified 🎉. Continue your checkout.");
		GTMCheckoutUserLoginOtpVerifySuccess(checkoutAnalyticsData, userData);
		if (registerFormCache) {
			const sp = new URLSearchParams(location.search);
			const ccc = (sp.get("cc") || checkoutAnalyticsData?.CCC || "").trim();
			const learnerSnap = {
				learner_dob: registerFormCache?.dob,
				learner_qualificationlevel: registerFormCache?.education,
			};
			if (!videoConsentDone && needsVideoConsent(learnerSnap, ccc)) {
				resumeProceedRef.current = (b64) => {
					setVideoConsentDone(true);
					handleContinue.mutate({
						...registerFormCache,
						recorded_video: b64 || recordedVideoRef.current || recordedVideoB64 || "",
					});
				};
				if (hasRecording()) setShowPreview(true);
				else setShowConsent(true);
				return;
			}
			handleContinue.mutate({
				...registerFormCache,
				recorded_video: recordedVideoRef.current || recordedVideoB64 || "",
			});
		} else {
			// Fallback: try to pull fresh values from the form if cache is missing
			// (only if you want to guard against unexpected states)
		}
	};

	const handleLoginSuccess = (data) => {
		toast.success("You're login successfully 🎉. Continue your checkout.");
		populateRegformByUser(data);

		const newSearchParam = [];
		for (let entry of searchParams.entries()) {
			newSearchParam.push(entry);
		}
		if (newSearchParam.uuidv4) {
			delete newSearchParam.uuidv4;
		}

		// replace current url to clear singpass query param if they is any
		window.scrollTo({ top: 0 });
		window.history.replaceState(
			null,
			null,
			createSearchParams(
				getUrlParams(newSearchParam, checkoutAnalyticsData, courserun_id),
			).toString(),
		);
		navigate(
			{
				pathname: "/checkout/account",
				search: createSearchParams(
					getUrlParams(newSearchParam, checkoutAnalyticsData, courserun_id),
				).toString(),
			},
			{
				replace: true,
			},
		);
	};

	const handleSingpassLogin = (data) => {
		GTMCheckoutSingpassRetrieveInfoButton(checkoutAnalyticsData, userData);
		window.location.href = API_URL + "/api/generateSingpassUrl?uuidv4=" + uuidv4();
	};

	const fetchSingpassData = () => {
		const searchParams = new URLSearchParams(window.location.search);

		const newSearchParam = [];
		for (let entry of searchParams.entries()) {
			newSearchParam.push(entry);
		}

		var uuidv4 = searchParams.get("uuidv4");
		if (searchParams.get("f") == 1) {
			generalApi
				.fetchSingpassData({
					uuidv4: uuidv4,
				})
				.then((singpassObj) => {
					const singpassUser = singpassObj.data[0];
					if (singpassUser) {
						console.warn("Singpass response is empty.");
					}

					const singpassAddress = JSON.parse(singpassUser.address);
					const newFormValue = {
						name: singpassUser.name,
						nric: singpassUser.nric,
						dob: singpassUser.dob,
						nationality: singpassUser.nationality,
						gender: singpassUser.gender,
						email: singpassUser.email,
						mobile: singpassUser.mobile,
						education: "",
						designation: singpassUser.designation,
						postal_code: singpassAddress?.postal?.value,
						street: singpassAddress?.street?.value,
						block: singpassAddress?.block?.value,
						floor: singpassAddress?.floor?.value,
						unit: singpassAddress?.unit?.value,
						building: singpassAddress?.building?.value,
						country: singpassAddress?.country?.desc,
						fill_with_singpass: true,
					};

					GTMCheckoutSingpassRetrieveInfoSuccess(checkoutAnalyticsData, {
						user: {
							learnermaster_id: null,
							learnermaster_name: newFormValue.name,
							learnermaster_dob: newFormValue.dob,
							learnermaster_nationality: newFormValue.nationality,
							learnermaster_gender: newFormValue.gender,
						},
					});

					// lock all the fields that are not empty from singpass response
					const fieldsToLock = Object.fromEntries(
						Object.entries(newFormValue).filter(([field_name, value]) => {
							// if it's a boolean value (true or false), then lock the field
							if (["email", "mobile"].includes(field_name)) {
								return false;
							}

							if (typeof value === "boolean" || typeof value === "number") {
								return true;
							}

							// else lock all the 'true' value (string) but not those 'falsy' value (empty string, null, undefined)
							else return Boolean(value);
						}),
					);

					reset(newFormValue);
					setLockedField(fieldsToLock);
					setisSingpass(true);
					window.history.replaceState(
						null,
						null,
						`/checkout/account?${createSearchParams(
							getUrlParams(newSearchParam, checkoutAnalyticsData, courserun_id),
						).toString()}`,
					);
				});
		} else if (searchParams.get("netrust_error") == 1) {
			// error
			GTMCheckoutSingpassRetrieveInfoFailed(checkoutAnalyticsData);
			toast.error("Singpass retrieve info failed, Please try again");
			window.history.replaceState(
				null,
				null,
				`/checkout/account?${createSearchParams(
					getUrlParams(newSearchParam, checkoutAnalyticsData, courserun_id),
				).toString()}`,
			);
		} else {
			window.history.replaceState(
				null,
				null,
				`/checkout/account?${createSearchParams(
					getUrlParams(newSearchParam, checkoutAnalyticsData, courserun_id),
				).toString()}`,
			);
			// toast.error("Singpass retrieve into failed, Please try again");
		}
	};

	/**
	 * auto populate the form if has regform cache or user data
	 */
	const [showRequireLoan, setRequireLoan] = useSessionStorageState(
		"checkout_required_laptop",
		false,
	);
	// Ensure CCC is NOT carried over from a previous session unless explicitly present in the URL
	useEffect(() => {
		const ccFromUrl = (searchParams.get("cc") || "").trim();
		// Always normalize CCC to the URL value ("" if absent) on first load
		dispatch(
			saveCheckoutAnalyticsData({
				...checkoutAnalyticsData,
				CCC: ccFromUrl,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (registerForm || learners) {
			setEditMode(false);
			reset({
				name: learners.learner_name,
				nric: learners.learner_nric,
				dob: learners.learner_dob,
				nationality: learners.learner_nationality,
				gender: learners.learner_gender,
				email: learners.learner_email,
				mobile: learners.learner_mobile,
				education: learners.learner_qualificationlevel,
				designation: learners.learner_designation,
				postal_code: learners.learner_postal,
				laptopLoan: learners.learner_laptoploan,
				street: learners.learner_street,
				unit: learners.learner_unit,
				block: learners.learner_block,
				floor: learners.learner_floor,
				building: learners.learner_building,
				country: learners.learner_country,
				fill_with_singpass: learners.fill_with_singpass,
			});
		} else if (registerFormCache) {
			reset({ ...registerFormCache });
		} else if (userData) {
			populateRegformByUser(userData);
		}
		fetchSingpassData();
	}, [userData?.user?.learnermaster_account_type]);

	useEffect(() => {
		if (checkoutAnalyticsData && checkoutAnalyticsData.course_id !== null && isFirstTime) {
			GTMCheckoutStep1Account(checkoutAnalyticsData, userData);
			setIsFirstTime(false);
		}

		// analytics for close tab
		const handleTabClosing = () => {
			if (checkoutAnalyticsData && checkoutAnalyticsData.course_id !== null) {
				GTMCheckoutStep1AccountExit(checkoutAnalyticsData, userData);
			}
		};

		window.addEventListener("unload", handleTabClosing);
		return () => {
			window.removeEventListener("unload", handleTabClosing);
		};
	}, [checkoutAnalyticsData, userData]);

	console.log("userdata", userData);
	useEffect(() => {
		setVideoConsentDone(false);
	}, [checkoutAnalyticsData?.CCC, learners?.learner_dob, learners?.learner_qualificationlevel]);

	useEffect(() => {
		// Prefer the persisted sessionStorage clip if present
		if (recordedVideoB64) {
			recordedVideoRef.current = recordedVideoB64;
			return;
		}
		// Otherwise hydrate from Redux cache (which we now keep in sync)
		if (registerFormCache?.recorded_video) {
			setRecordedVideoB64(registerFormCache.recorded_video);
			recordedVideoRef.current = registerFormCache.recorded_video;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recordedVideoB64, registerFormCache]);

	useEffect(() => {
		// if course/run changed, clear old clip (optional policy)
		setRecordedVideoB64("");
		recordedVideoRef.current = "";
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [courserun_id]);

	// Age by YEAR ONLY: "2025" - "1958" => 67 (no month/day check)
	function ageByYearOnly(dobString, refYear = new Date().getFullYear()) {
		if (!dobString) return null;
		const birthYear = parseInt(String(dobString).slice(0, 4), 10);
		if (Number.isNaN(birthYear)) return null;
		return refYear - birthYear;
	}

	// Full DOB vs specific date (e.g. courserun_course_from) in YYYY-MM-DD
	function ageAtDateExact(dobString, refDateISO) {
		if (!dobString || !refDateISO) return null;
		const b = new Date(dobString);
		const r = new Date(refDateISO);
		if (Number.isNaN(b.getTime()) || Number.isNaN(r.getTime())) return null;

		let age = r.getFullYear() - b.getFullYear();
		const m = r.getMonth() - b.getMonth();
		if (m < 0 || (m === 0 && r.getDate() < b.getDate())) age -= 1;
		return age;
	}

	// Mixed rule required:
	// - 70+ by YEAR-ONLY at course year
	// - 67–69 by EXACT date at course date
	function isSeniorAtRun(dobString, runDateISO) {
		if (!dobString || !runDateISO) return false;
		const refYear = new Date(runDateISO).getFullYear();
		const byYear = ageByYearOnly(dobString, refYear); // year-only
		const exact = ageAtDateExact(dobString, runDateISO); // full yyyy-mm-dd
		// 70+ by year, OR 67–69 by exact
		return (
			(typeof byYear === "number" && byYear >= 70) ||
			(typeof exact === "number" && exact >= 67 && exact < 70)
		);
	}

	function deriveVideoVariant({ dob, designation, qualification, courseId, runDate }) {
		// senior: 70+ (year-only) OR 67–69 (exact by run date), OR designation contains "retire"
		const senior = isSeniorAtRun(dob, runDate) || /retire/i.test(designation || "");

		const isSpecial = SPECIAL_COURSE_IDS.includes(Number(courseId || 0));
		const q = String(qualification || "")
			.trim()
			.toUpperCase();
		const mer = isSpecial
			? q === "BELOW PSLE"
			: LOWER_EDU_LIST.some((s) => q.includes(s.toUpperCase()));

		let variant = "NONE";
		if (!senior && mer) variant = "MER";
		else if (senior && !mer) variant = "SENIOR";
		else if (senior && mer) variant = "SENIOR_MER";

		const specialENStr = isSpecial ? "PSLE" : "N levels";

		// keep age for display (use exact @ run date where available, else year-only now)
		const ageExact = ageAtDateExact(dob, runDate);
		const ageYearOnly = ageByYearOnly(dob, runDate ? new Date(runDate).getFullYear() : undefined);
		const age = typeof ageExact === "number" ? ageExact : ageYearOnly;

		return { variant, isSpecial, specialENStr, age, senior, mer };
	}

	// true if value equals/contains "Primary Qualification (e.g PSLE) or Below"
	function isPrimaryOrBelow(educationValueOrLabel) {
		if (!educationValueOrLabel) return false;
		const s = String(educationValueOrLabel).toLowerCase().trim();
		return s === "psle" || s.includes("below psle");
	}

	function needsVideoConsent(learner, ccc) {
		const hasCC = (ccc || "").trim().length > 0;

		// Exact age at course date
		const runDate = courserun?.courserun_course_from;
		const exact = ageAtDateExact(learner?.learner_dob, runDate);

		// Gate only for 67–69 by exact DOB vs run date
		const ageGate = typeof exact === "number" && exact >= 67;

		const refYear = new Date(runDate).getFullYear();
		const byYear = ageByYearOnly(learner?.learner_dob, refYear);

		const eduGate = isPrimaryOrBelow(learner?.learner_qualificationlevel);
		return hasCC && (ageGate || eduGate);
	}

	return (
		<>
			{userData || isSingpass ? <CheckoutLogoutButton /> : null}
			<div className={classNames(styles.root, "shadow-sm")}>
				<SuccessCreateAccountModal
					onNext={async () => {
						createdAccountRef.current = false;
						setcreateAccountSuccessModal(false);
						await proceedToPaymentCore(); // ✅ age → consent (if needed) → grant → navigate
					}}
					isOpen={createAccountSuccessModal}
					toggle={toggleSuccessCreateAccountModal}
				/>
				<LoginModal isOpen={loginModal} toggle={toggleLoginModal} onSuccess={handleLoginSuccess} />
				{userData ? (
					<PreventCorporateAccountModal
						isOpen={userData.user && userData.user.learnermaster_account_type === "corporate"}
						user={userData?.user}
					/>
				) : null}
				{otpmodal ? <OTPModalSignup onSuccess={handleOTPSuccess} /> : null}
				{!userData && !isSingpass ? (
					<div className="mb-4">
						<h2 className="fs30 text-color-dark mb-3">
							Thank you for your interest in our courses. Account registration is required for you
							to complete your enrollment online.
						</h2>
						<p className="fs15 text-color-dark">
							Course fee funding is available for self-sponsored individuals.
						</p>
						<p className="fs15 text-color-dark">
							To accurately calculate your Course Fee Funding based on your eligiblity and complete
							the enrolment, please register for an account.
						</p>
						<p className="fs15 text-color-dark">
							For <span className="text-fca-green font-weight-600">new users</span>, please click on
							"Retrieve Myinfo with Singpass" to proceed with your account registration.
						</p>
						<p className="fs15 text-color-dark">
							For <span className="text-fca-green font-weight-600">existing users</span>, please
							click on the login button below to proceed.
						</p>
						<p className="fs15 text-color-dark">
							For <span className="text-fca-blue font-weight-600">users with no Singpass</span>, you
							may contact us via our hotline at{" "}
							<a href={`tel:${CONTACT_NO_CHECKOUT}`}>{CONTACT_NO_CHECKOUT}</a> or leave us a message
							via our{" "}
							<a
								href="#enquiry-form"
								onClick={(event) => {
									event.preventDefault();
									openEnquiryModal();
								}}>
								enquiry form
							</a>
							. Our education consultants will reach out to you within one working day.
						</p>

						<div className={`text-center text-md-left ${styles.login_button_container}`}>
							<div className={styles.singpass_login_container}>
								<div
									onClick={handleSingpassLogin}
									className={`${styles.button_wrap} ${
										isSingPassUnderMaintenance ? styles.disabled : ""
									}`}>
									<img
										src={SingpassButton}
										alt="Singpass Myinfo"
										className={styles.singpass_login_button}
									/>
								</div>
								{isSingPassUnderMaintenance && (
									<div className="invalid-feedback d-inline-block">
										MyInfo is currently under maintenance, please check back again later
									</div>
								)}
							</div>
							{/* <div className={styles.self_login_container}> */}
							<div className={styles.button_wrap}>
								<Button
									className={styles.user_login_btn}
									onClick={() => {
										toggleLoginModal();
										GTMCheckoutUserLoginButton(checkoutAnalyticsData, userData);
									}}>
									Existing User Login
								</Button>
							</div>

							{/* </div> */}
						</div>
					</div>
				) : (
					<div>
						{userData && !userData.user.email_verified_at ? (
							<Alert>
								<p>
									Seems like your email is not verified yet. Please verify now to continue the
									checkout
								</p>
								<Button
									onClick={() => {
										GTMCheckoutUserLoginOtpVerify(checkoutAnalyticsData, userData);
										setOTPModal(true);
									}}>
									Verify Email
								</Button>
							</Alert>
						) : null}

						<h3 className={styles.section_header}>Personal Particulars</h3>
						<Form onSubmit={handleSubmit(handleSubmitContinue)}>
							<Row>
								<Col lg={6}>
									<FormGroup>
										<CourseConsultantCode
											value={checkoutAnalyticsData?.CCC || ""}
											onChange={(ccCode) =>
												dispatch(
													saveCheckoutAnalyticsData({
														...checkoutAnalyticsData,
														CCC: (ccCode || "").trim(),
													}),
												)
											}
										/>
									</FormGroup>
								</Col>
							</Row>
							<Row>
								<Col lg={6}>
									<FormGroup>
										<Label>NRIC or ID No.</Label>
										<input
											disabled={!editMode || lockedField.nric}
											className={"form-control " + (errors.nric ? "is-invalid" : "")}
											{...register("nric")}
											placeholder="NRIC"
										/>
										<FormFeedback>{errors.nric ? errors.nric.message : null}</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={6}>
									<FormGroup>
										<Label>Full Name (As Shown in NRIC)</Label>
										<input
											disabled={!editMode || lockedField.name}
											className={"form-control " + (errors.name ? "is-invalid" : "")}
											{...register("name")}
											placeholder="Full Name (As Shown in NRIC)"
										/>
										<FormFeedback>{errors.name ? errors.name.message : null}</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={6}>
									<FormGroup>
										<Label>Date of Birth</Label>
										<input
											disabled={!editMode || lockedField.dob}
											className={"form-control " + (errors.dob ? "is-invalid" : "")}
											type="date"
											placeholder="Date of Birth"
											{...register("dob")}
										/>
										<FormFeedback>{errors.dob ? errors.dob.message : null}</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={6}>
									<FormGroup>
										<Label>Nationality</Label>
										<select
											disabled={!editMode || lockedField.nationality}
											className={"form-control " + (errors.nationality ? "is-invalid" : "")}
											type="select"
											{...register("nationality")}>
											{NATIONALITY_OPTIONS.map((item) => {
												return (
													<option key={item.value} value={item.value}>
														{item.label}
													</option>
												);
											})}
										</select>
										<FormFeedback>
											{errors.nationality ? errors.nationality.message : null}
										</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={4}>
									<FormGroup>
										<Label>Email Addresss</Label>
										<input
											disabled={!editMode || lockedField.email}
											className={"form-control " + (errors.email ? "is-invalid" : "")}
											{...register("email")}
											placeholder="example@example.com"
										/>
										<FormFeedback>{errors.email ? errors.email.message : null}</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={4}>
									<FormGroup>
										<Label>Mobile No.</Label>
										<input
											disabled={!editMode || lockedField.mobile}
											className={"form-control " + (errors.mobile ? "is-invalid" : "")}
											{...register("mobile")}
											placeholder="Mobile Number"
										/>
										<FormFeedback>{errors.mobile ? errors.mobile.message : null}</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={4}>
									<FormGroup>
										<Label>Gender</Label>
										<select
											disabled={!editMode || lockedField.gender}
											className={"form-control " + (errors.gender ? "is-invalid" : "")}
											{...register("gender")}
											type="select"
											placeholder="Gender">
											<option value="">Select an option</option>
											{GENDER_OPTIONS.map((item) => {
												return (
													<option key={item.value} value={item.value}>
														{item.label}
													</option>
												);
											})}
										</select>
										<FormFeedback>{errors.gender ? errors.gender.message : null}</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={12}>
									<h3 className={classNames(styles.section_header, "mb-4")}>Address</h3>
								</Col>

								<Col lg={12}>
									<FormGroup>
										<Label>Street</Label>
										<input
											disabled={!editMode || lockedField.street}
											className={"form-control " + (errors.street ? "is-invalid" : "")}
											placeholder="Street"
											{...register("street")}
										/>
									</FormGroup>
									<FormFeedback>{errors.street ? errors.street.message : null}</FormFeedback>
								</Col>

								<Col lg={12}>
									<FormGroup>
										<Label>Building Name</Label>
										<input
											disabled={!editMode || lockedField.building}
											className={"form-control " + (errors.building ? "is-invalid" : "")}
											placeholder="building"
											{...register("building")}
										/>
									</FormGroup>
									<FormFeedback>{errors.building ? errors.building.message : null}</FormFeedback>
								</Col>

								<Col xs={12} md={4}>
									<FormGroup>
										<Label>Block</Label>
										<input
											disabled={!editMode || lockedField.block}
											className={"form-control " + (errors.block ? "is-invalid" : "")}
											placeholder="Block"
											{...register("block")}
										/>
									</FormGroup>
									<FormFeedback>{errors.block ? errors.block.message : null}</FormFeedback>
								</Col>

								<Col xs={6} md={4}>
									<FormGroup>
										<Label>Floor</Label>
										<input
											disabled={!editMode || lockedField.floor}
											className={"form-control " + (errors.floor ? "is-invalid" : "")}
											placeholder="floor"
											{...register("floor")}
										/>
									</FormGroup>
									<FormFeedback>{errors.floor ? errors.floor.message : null}</FormFeedback>
								</Col>

								<Col xs={6} md={4}>
									<FormGroup>
										<Label>Unit No</Label>
										<input
											disabled={!editMode || lockedField.unit}
											className={"form-control " + (errors.unit ? "is-invalid" : "")}
											placeholder="Unit No"
											{...register("unit")}
										/>
										<FormFeedback>{errors.unit ? errors.unit.message : null}</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={6}>
									<FormGroup>
										<Label>Postal Code</Label>
										<input
											disabled={!editMode || lockedField.postal_code}
											className={"form-control " + (errors.postal_code ? "is-invalid" : "")}
											placeholder="Postal Code"
											{...register("postal_code")}
										/>
										<FormFeedback>
											{errors.postal_code ? errors.postal_code.message : null}
										</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={6}>
									<FormGroup>
										<Label>Country</Label>
										<select
											disabled={!editMode || lockedField.country}
											className={"form-control " + (errors.country ? "is-invalid" : "")}
											type="select"
											{...register("country")}>
											{COUNTRIES_OPTIONS.map((item) => {
												return (
													<option key={item.value} value={item.value}>
														{item.label}
													</option>
												);
											})}
										</select>
										<FormFeedback>{errors.country ? errors.country.message : null}</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={12}>
									<FormGroup>
										<Label>Education/ Qualification Level</Label>
										<select
											disabled={!editMode || lockedField.education}
											className={"form-control " + (errors.education ? "is-invalid" : "")}
											type="select"
											placeholder="Select Your Education/ Qualifications"
											{...register("education")}>
											<select value="">Select your qualification level</select>
											{QUALIFICATOIN_LEVEL_OPTION.map((item) => {
												return (
													<option key={item.value} value={item.value}>
														{item.label}
													</option>
												);
											})}
										</select>
										<FormFeedback>
											{errors.education ? errors.education.message : null}
										</FormFeedback>
									</FormGroup>
								</Col>

								<Col lg={12}>
									<FormGroup>
										<Label>Occupation</Label>
										<input
											disabled={!editMode || lockedField.designation}
											className={"form-control " + (errors.designation ? "is-invalid" : "")}
											placeholder="e.g. Outlet Manager"
											{...register("designation")}
										/>
										<FormFeedback>
											{errors.designation ? errors.designation.message : null}
										</FormFeedback>
									</FormGroup>
								</Col>

								<Col xs={12}>
									<Divider className="my-5" style={{ marginLeft: -20, marginRight: -20 }} />
								</Col>

								<Col lg={12}>
									<h3 className={classNames(styles.section_header, "mb-4")}>Laptop Loan</h3>
								</Col>
								<Col lg={12}>
									<FormGroup>
										<Label>
											As our courses required the usage of laptop. Do you have your own laptop for
											class?
										</Label>
										<select
											disabled={!editMode}
											defaultValue="1"
											className={"form-control " + (errors.laptopLoan ? "is-invalid" : "")}
											onChange={(requireLoan) => {
												if (requireLoan.target.value == "0") {
													setRequireLoan(true);
													setValue("laptopLoan", "0"); // make sure we clear the value
												} else {
													setRequireLoan(false);
													setValue("laptopLoan", "0"); // make sure we clear the value
												}
											}}>
											<option value="1">Yes</option>
											<option value="0">No</option>
										</select>
										<FormFeedback>
											{errors.laptopLoan ? errors.laptopLoan.message : null}
										</FormFeedback>
									</FormGroup>
								</Col>

								{showRequireLoan && (
									<Col lg={12}>
										<FormGroup>
											<Label>Do you require to loan a laptop?</Label>
											<select
												disabled={!editMode}
												className={"form-control"}
												{...register("laptopLoan")}>
												{LOAN_OPTIONS.map((item) => {
													return (
														<option key={item.value} value={item.value}>
															{item.label}
														</option>
													);
												})}
											</select>
											<FormFeedback>
												{errors.laptopLoan ? errors.laptopLoan.message : null}
											</FormFeedback>
										</FormGroup>
									</Col>
								)}

								{!userData ? (
									<>
										<Col xs={12}>
											<Divider className="my-5" style={{ marginLeft: -20, marginRight: -20 }} />
										</Col>

										<Col lg={6}>
											<FormGroup id="password_box">
												<Label>Password</Label>
												<InputGroup
													className={
														"input-group-merge " +
														(errors.password ? "needs-validation is-invalid" : "")
													}>
													<InputGroupAddon addonType="prepend">
														<InputGroupText
															className={"form-control " + (errors.password ? "is-invalid" : "")}>
															{/* <i className='ni ni-lock-circle-open' /> */}
															<LockIcon />
														</InputGroupText>
													</InputGroupAddon>
													<input
														autoComplete="new-password"
														placeholder="Password"
														type={showPassword ? "text" : "password"}
														className={"form-control " + (errors.password ? "is-invalid" : "")}
														{...register("password")}
													/>
													<InputGroupAddon addonType="append">
														<InputGroupText
															onClick={() => {
																setShowPassword(!showPassword);
															}}>
															{showPassword ? (
																<i className="far fa-eye-slash"></i>
															) : (
																<i className="far fa-eye"></i>
															)}
														</InputGroupText>
													</InputGroupAddon>
												</InputGroup>

												<div className="invalid-feedback">{errors.password?.message}</div>
												<UncontrolledTooltip placement="right" target="password_box">
													<div>- At least 1 Upper case character</div>
													<div>- At least 1 Lower case character</div>
													<div>- At least 1 Numeric character</div>
													<div>- At least 1 Special character</div>
												</UncontrolledTooltip>
											</FormGroup>
										</Col>

										<Col lg={6}>
											<FormGroup>
												<Label>Confirm Password</Label>
												<InputGroup
													className={
														"input-group-merge " +
														(errors.password_confirmation ? "needs-validation is-invalid" : "")
													}>
													<InputGroupAddon addonType="prepend">
														<InputGroupText
															className={
																"form-control " + (errors.password_confirmation ? "is-invalid" : "")
															}>
															{/* <i className='ni ni-lock-circle-open' /> */}
															<LockIcon />
														</InputGroupText>
													</InputGroupAddon>
													<input
														autoComplete="new-password"
														placeholder="Password"
														type={showPassword ? "text" : "password"}
														className={
															"form-control " + (errors.password_confirmation ? "is-invalid" : "")
														}
														{...register("password_confirmation")}
													/>
													<InputGroupAddon addonType="append">
														<InputGroupText
															onClick={() => {
																setShowPassword(!showPassword);
															}}>
															{showPassword ? (
																<i className="far fa-eye-slash"></i>
															) : (
																<i className="far fa-eye"></i>
															)}
														</InputGroupText>
													</InputGroupAddon>
												</InputGroup>
												<div className="invalid-feedback">
													{errors.password_confirmation?.message}
												</div>
											</FormGroup>
										</Col>
									</>
								) : null}
								<Col>{errorData ? errorData.element : null}</Col>
								<Row className="w-100 justify-content-end ml-0">
									{editMode ? (
										<>
											{handleContinue.isLoading ? (
												<Col className="mt-2" md={12} lg={"auto"}>
													<Button type="submit" className={classNames("btn-gradient")}>
														<Spinner />
													</Button>
												</Col>
											) : (
												<Col className="mt-2" md={12} lg={"auto"}>
													<Button type="submit" className={classNames("btn-gradient")}>
														{!userData ? "Create Account" : "Continue"}
													</Button>
												</Col>
											)}
										</>
									) : (
										<>
											<Col className="mt-2" md={12} lg={"auto"}>
												<Button className="w-100" outline color="primary" onClick={handleClickEdit}>
													I want to update my details
												</Button>
											</Col>
											<Col className="mt-2" md={12} lg={"auto"}>
												<Button
													className={classNames("btn-gradient", "w-100")}
													onClick={handleProceedToPaymentPage}>
													Proceed to Payment
												</Button>
											</Col>
										</>
									)}
								</Row>
							</Row>
						</Form>
					</div>
				)}
			</div>
			<ConsentModal
				isOpen={showConsent}
				onCancel={() => setShowConsent(false)}
				onConfirm={() => {
					setShowConsent(false);
					setShowVideo(true);
				}}
				// NEW:
				variant={videoCtx.variant}
				specialENStr={videoCtx.specialENStr}
				learnerName={learners?.learner_name}
				age={videoCtx.age}
			/>
			<VideoRecorderModal
				isOpen={showVideo}
				onClose={() => setShowVideo(false)}
				onRecorded={async (blob) => {
					const fr = new FileReader();
					fr.onloadend = async () => {
						const b64 = typeof fr.result === "string" ? fr.result : "";

						// Keep your existing caching
						recordedVideoRef.current = b64;
						setRecordedVideoB64(b64);
						dispatch(cacheRegform({ ...(registerFormCache || {}), recorded_video: b64 }));

						// NEW: upload now if we already have a registrationform_id
						try {
							// Use the same encrypted id you already keep in redux/store
							if (registrationform_id) {
								await generalApi.saveMERSeniorVideo({
									registrationform_id, // encrypted id from your store
									recorded_video: b64, // "data:video/...;base64,...."
									from_senior_popup:
										videoCtx.variant === "SENIOR" || videoCtx.variant === "SENIOR_MER" ? 1 : 0,
								});
								// Optional: toast to confirm background save
								// toast.success("Video saved.");
							}
						} catch (e) {
							// Do NOT block the flow; just log or show a soft notice.
							console.warn("saveMERSeniorVideo failed", e);
							// Optional: toast.warn("We couldn't upload the video right now. It'll still be attached on submit.");
						}

						setShowVideo(false);
						// Resume whatever action was waiting (Continue / Proceed)
						resumeProceedRef.current?.(b64);
					};
					fr.readAsDataURL(blob);
				}}
				variant={videoCtx.variant}
				specialENStr={videoCtx.specialENStr}
			/>
			<RecordingPreviewModal
				isOpen={showPreview}
				b64={recordedVideoB64}
				onUse={async () => {
					setShowPreview(false);
					const b64 = recordedVideoRef.current || recordedVideoB64 || "";

					// Optional: attempt upload again if we now have a regform id
					try {
						if (registrationform_id && b64) {
							await generalApi.saveMERSeniorVideo({
								registrationform_id,
								recorded_video: b64,
								from_senior_popup:
									videoCtx.variant === "SENIOR" || videoCtx.variant === "SENIOR_MER" ? 1 : 0,
							});
							dispatch(cacheRegform({ ...(registerFormCache || {}), recorded_video: b64 }));
						}
					} catch (e) {
						console.warn("saveMERSeniorVideo (preview/use) failed", e);
					}

					resumeProceedRef.current?.(b64);
				}}
				onRecapture={startRecapture}
			/>
			<SeniorKycConsentModal
				isOpen={showKycConsent}
				onClose={() => {
					setShowKycConsent(false);
					setSeniorNeedsReview(false);
				}}
				onConsent={() => {
					setShowKycConsent(false);
					setShowSeniorForm(true); // 2) after consent, show form
				}}
			/>
			<SeniorReviewModal
				isOpen={showSeniorForm}
				initialData={seniorPrefill}
				onClose={() => {
					setShowSeniorForm(false);
					setSeniorNeedsReview(false);
				}}
				onConfirm={async (payload) => {
					try {
						const res = await generalApi.sendKycApproval({
							name: payload?.name,
							contact: payload?.mobile,
							dob: payload?.dob, // dd/mm/yyyy or yyyy-mm-dd
							courserun_id: courserun_id,
							note: payload?.note || "",
						});

						if (res?.ok) {
							// close the review modal
							setShowSeniorForm(false);

							// build success HTML (keeps same modal style using dangerouslySetInnerHTML)
							const html = `
								<div>
									<p><strong>Submitted successfully.</strong></p>
									<p>Thanks! Your KYC registration request has been submitted. Our team will contact you shortly.</p>
									${res.id ? `<p style="margin-top:8px;">Reference No.: <strong>#${res.id}</strong></p>` : ""}
								</div>
							`;
							setKycSuccessHtml(html);
							setKycSuccessOpen(true);
						} else {
							// optional: show a toast or reuse your failure modal
							// toast.error(res?.message || "Submission failed");
						}
					} catch (e) {
						// optional: toast.error("Network error");
						console.warn("sendKycApproval error", e);
					}
				}}
			/>
			<Modal isOpen={kycSuccessOpen} centered>
				<ModalHeader>Submission Received</ModalHeader>
				<ModalBody>
					<div dangerouslySetInnerHTML={{ __html: kycSuccessHtml }} />
				</ModalBody>
				<ModalFooter>
					{/* Option A: just close */}
					<Button color="primary" onClick={() => setKycSuccessOpen(false)}>
						OK
					</Button>

					{/* Option B: send them somewhere (uncomment if you want redirect) */}
					{/*
						<Link to={to_official_website_url("/courses/")} replace>
						<Button color="primary">Return to courses</Button>
						</Link>
					*/}
				</ModalFooter>
			</Modal>
		</>
	);
}

const loginSchema = yup
	.object({
		email: yup.string().email().required(),
		password: yup.string().required(),
	})
	.required();

function ConsentModal({ isOpen, onCancel, onConfirm, variant, specialENStr, learnerName, age }) {
	const isSenior = variant === "SENIOR" || variant === "SENIOR_MER";
	const hasMerText = variant === "MER" || variant === "SENIOR_MER";

	console.log("ConsentModal render", {
		variant,
		isSenior,
		hasMerText,
		specialENStr,
		learnerName,
		age,
	});

	return (
		<Modal isOpen={isOpen} toggle={onCancel} centered size="lg">
			<ModalHeader toggle={onCancel}>
				{hasMerText ? "Verification Recording Consent" : "Before You Continue"}
			</ModalHeader>

			<ModalBody>
				{variant === "MER" && (
					<div className="ml-2 mr-2">
						<p className="mb-4 text-left">
							Hi <strong>{learnerName || "Learner"}</strong>,
						</p>
						<p className="text-left">Thank you for your interest in our courses! 😊</p>
						<p className="text-left">As part of our standard enrolment process, please note:</p>
						<ul className="text-left">
							<li>
								Our WSQ courses are designed for participants who meet the Minimum Entry Requirement
								(MER) of {specialENStr} qualification or equivalent literacy and numeracy skills
							</li>
							<li>
								We'll ask a few short questions to confirm your interest and understanding of the
								course details
							</li>
							<li>This ensures that every learner is well-informed before enrolment</li>
							<li>The session will be recorded for verification purposes</li>
						</ul>
						<p className="text-left mt-4">
							<strong>Do you consent to this recording? 🎥</strong>
						</p>
					</div>
				)}

				{variant === "SENIOR" && (
					<div className="ml-2 mr-2">
						<p className="mb-4 text-left">
							Hi <strong>{learnerName || "Learner"}</strong>,
						</p>
						<p className="text-left">Before You Continue 📝</p>
						<p className="text-left">
							Our WSQ courses are designed to help learners gain practical skills for personal and
							career development.
						</p>
						<p className="text-left">
							Before you proceed with your enrolment, please review the key course requirements:
						</p>
						<ul className="text-left">
							<li>
								<strong>Purpose</strong> – WSQ courses aim to help participants build new skills
								that can support current and future job or personal growth opportunities
							</li>
							<li>
								<strong>Language</strong> – Lessons will be conducted in English
							</li>
							<li>
								<strong>Technology Use</strong> – Learners should be comfortable using a computer
								and smartphone for class activities
							</li>
							<li>
								<strong>Assessment</strong> – There will be an assessment at the end of the course
								to evaluate understanding
							</li>
						</ul>
						<p className="text-left">
							If you understand and are comfortable with the above, we'll do a short video
							confirmation to ensure everything has been clearly explained and you're ready to
							proceed with your enrolment.
						</p>
					</div>
				)}

				{variant === "SENIOR_MER" && (
					<div className="ml-2 mr-2">
						<p className="mb-4 text-left">
							Hi <strong>{learnerName || "Learner"}</strong>,
						</p>
						<p className="text-left">Before You Continue 📝</p>
						<p className="text-left">Thank you for your interest in our WSQ courses! 😊</p>
						<p className="text-left">
							Our WSQ courses are designed to help learners gain practical, work-relevant skills for
							personal and career development.
						</p>
						<p className="text-left">
							Before proceeding with your enrolment, please review these important details:
						</p>
						<ul className="text-left">
							<li>
								<strong>Minimum Entry Requirement (MER)</strong> – Our courses are suitable for
								participants with an {specialENStr} qualification or equivalent literacy and
								numeracy skills
							</li>
							<li>
								<strong>Purpose</strong> – WSQ courses aim to help participants build new skills
								that can support both current and future job or personal growth opportunities
							</li>
							<li>
								<strong>Language</strong> – Lessons will be conducted in English
							</li>
							<li>
								<strong>Technology Use</strong> – Learners should be comfortable using a computer
								and smartphone for class activities
							</li>
							<li>
								<strong>Assessment</strong> – There will be an assessment at the end of the course
								to evaluate understanding and learning outcomes
							</li>
						</ul>
						<p className="text-left">
							To confirm your interest and ensure everything has been clearly explained, we'll
							conduct a short video confirmation with a few quick questions. This recording is done
							for verification purposes only.
						</p>
						<p className="text-left mt-3">
							<strong>📹 Do you consent to this verification recording?</strong>
						</p>
					</div>
				)}
			</ModalBody>

			<ModalFooter className="d-flex w-100 justify-content-between">
				<Button color="secondary" onClick={onCancel}>
					Cancel
				</Button>
				<Button color="success" onClick={onConfirm}>
					Proceed
				</Button>
			</ModalFooter>
		</Modal>
	);
}

function VideoRecorderModal({ isOpen, onClose, onRecorded, variant, specialENStr }) {
	// --- Refs & state ---
	const videoRef = useRef(null);
	const mediaRecorderRef = useRef(null);
	const chunksRef = useRef([]);
	const streamRef = useRef(null);
	const stopTimeoutRef = useRef(null);

	const [stream, setStream] = useState(null);
	const [recording, setRecording] = useState(false);
	const [error, setError] = useState(null);

	// Save/Cancel intent as refs (sync, no stale closure)
	const saveOnStopRef = useRef(false);
	const canceledRef = useRef(false);

	// attach stream to <video> and auto-play
	async function attach(streamObj) {
		const v = videoRef.current;
		if (!v || !streamObj) return;
		// make autoplay-eligible before attaching (iOS/Safari)
		v.muted = true;
		v.playsInline = true;
		v.autoplay = true;
		v.setAttribute("muted", "");
		v.setAttribute("playsinline", "");
		v.setAttribute("autoplay", "");

		if ("srcObject" in v) v.srcObject = streamObj;
		else v.src = URL.createObjectURL(streamObj);

		try {
			await v.play();
		} catch (err) {
			console.log(err);
		}
	}

	function stopStream() {
		try {
			const s = streamRef.current || stream;
			if (s) {
				s.getTracks().forEach((t) => {
					try {
						t.stop();
					} catch (err) {
						console.log(err);
					}
				});
			}
		} catch (err) {
			console.log(err);
		}
		streamRef.current = null;
		setStream(null);

		const v = videoRef.current;
		if (v) {
			try {
				v.pause();
			} catch (err) {
				console.log(err);
			}
			// fully detach for Safari/WebKit
			try {
				v.srcObject = null;
			} catch (err) {
				console.log(err);
			}
			v.removeAttribute("srcObject");
		}
	}

	function finalizeNow() {
		const shouldExport = !canceledRef.current && saveOnStopRef.current && chunksRef.current.length;
		const blob = shouldExport ? new Blob(chunksRef.current, { type: "video/webm" }) : null;
		chunksRef.current = [];
		stopStream();
		if (shouldExport && blob) onRecorded?.(blob);
		setRecording(false);
		saveOnStopRef.current = false;
		canceledRef.current = false;
	}

	function stopRecorder(save = false) {
		try {
			const mr = mediaRecorderRef.current;
			saveOnStopRef.current = !!save;
			canceledRef.current = !save;

			if (mr && mr.state === "recording") {
				// Flush last chunk (important for Safari)
				try {
					mr.requestData();
				} catch (e) {
					console.log(e);
				}
				mr.stop();

				// Fallback finalize if onstop mysteriously doesn't fire
				clearTimeout(stopTimeoutRef.current);
				stopTimeoutRef.current = setTimeout(() => {
					finalizeNow();
				}, 1500);
			}
		} catch (err) {
			console.log(err);
		}
	}

	function hardCleanup({ save } = { save: false }) {
		// Do NOT stop tracks here; let onstop or fallback own that to avoid missing final events
		stopRecorder(!!save);
		// chunksRef cleared in onstop/fallback
	}

	// Cancel button should do full cleanup so LED goes off
	const handleCancel = () => {
		hardCleanup({ save: false });
		onClose?.();
	};

	// If parent closes modal (backdrop/escape), ensure we clean up
	useEffect(() => {
		if (!isOpen) {
			hardCleanup({ save: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Setup media (getUserMedia) when opened
	useEffect(() => {
		let cancelled = false;

		async function setup() {
			if (!isOpen) return;

			setError(null);
			saveOnStopRef.current = false;
			canceledRef.current = false;

			try {
				const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
				if (cancelled) return;
				setStream(s);
				streamRef.current = s;
				await attach(s);
			} catch (e) {
				setError(
					"We could not access your camera/microphone. Please allow permissions and try again.",
				);
			}
		}

		setup();

		return () => {
			try {
				clearTimeout(stopTimeoutRef.current);
				if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
					saveOnStopRef.current = false;
					canceledRef.current = true;
					mediaRecorderRef.current.stop();
				}
			} catch (err) {
				console.log(err);
			}
			stopStream();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	// Auto-start recording once we have a stream and modal is open
	useEffect(() => {
		if (!isOpen || !stream || recording) return;

		try {
			chunksRef.current = [];
			const mime =
				window.MediaRecorder &&
				MediaRecorder.isTypeSupported &&
				MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
					? "video/webm;codecs=vp8,opus"
					: "video/webm";

			const mr = new MediaRecorder(stream, { mimeType: mime });
			mediaRecorderRef.current = mr;

			mr.ondataavailable = (e) => {
				if (e?.data?.size) chunksRef.current.push(e.data);
			};

			mr.onstop = async () => {
				try {
					clearTimeout(stopTimeoutRef.current);
					const shouldExport =
						!canceledRef.current && saveOnStopRef.current && chunksRef.current.length;
					const blob = shouldExport ? new Blob(chunksRef.current, { type: "video/webm" }) : null;
					chunksRef.current = [];
					stopStream();
					if (shouldExport && blob) {
						await onRecorded?.(blob);
					}
				} finally {
					saveOnStopRef.current = false;
					canceledRef.current = false;
					setRecording(false);
				}
			};

			// Timeslice ensures periodic dataavailable events across browsers
			mr.start(250);
			setRecording(true);
		} catch (e) {
			setError(e?.message || "Failed to start recording.");
			setRecording(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, stream, recording]);

	const stopAndSave = () => {
		try {
			stopRecorder(true);
		} catch (e) {
			setError(e?.message || "Failed to stop recording.");
		}
	};

	// Lead line
	const LEAD_TEXT =
		"Please answer the following questions with a Yes or No to confirm your understanding before we proceed with your enrolment:";

	// Render variant questions (unchanged)
	function renderQuestions() {
		if (variant === "MER") {
			return (
				<>
					<h4 className="text-left mb-4 mt-1">
						Please read and answer the following verbally in the recording:
					</h4>
					<ul className="text-left">
						<li>
							Before we proceed, may we have your consent to record and confirm the important
							pointers discussed? This will serve as an official reference for future clarity.
						</li>
						<li className="mt-3">
							As our minimum entry requirement is at least {specialENStr} and above, we would like
							to share that you may potentially face challenges during the class. However, I
							understand that you are still keen to try out the class, correct?
						</li>
						<li className="mt-3">
							As discussed, our courses will be conducted in English, and you are aware of this?
						</li>
						<li className="mt-3">
							There will also be usage of laptops during the course, and you are aware of this?
						</li>
					</ul>
				</>
			);
		}

		if (variant === "SENIOR") {
			return (
				<>
					<h4 className="text-left mb-4 mt-1">{LEAD_TEXT}</h4>
					<ul className="text-left">
						<li>
							<strong>Verification Recording</strong>
							<p>
								Before we proceed, may we have your consent to record a short video to reconfirm the
								important points discussed?
							</p>
							<p>
								This recording will serve as an official reference for future clarity and will not
								be shared publicly.
							</p>
						</li>
						<li className="mt-3">
							<strong>Learning Goals</strong>
							<p>
								Our WSQ courses are designed to help you gain practical, work-relevant skills for
								your career and personal growth. Are you interested in learning these skills to
								support your goals?
							</p>
						</li>
						<li className="mt-3">
							<strong>Language, Technology Use and Assessment</strong>
							<p>
								Are you aware that lessons will be conducted in English, that you’ll be expected to
								use a computer and smartphone for class activities, and that there will be an
								assessment at the end of the course to evaluate your learning?
							</p>
						</li>
						<li className="mt-3">
							<strong>Acknowledgement</strong>
							<p>
								Do you confirm that all the above points have been clearly explained to you and that
								you are comfortable to proceed with the enrolment?
							</p>
						</li>
					</ul>
				</>
			);
		}

		// SENIOR_MER (default)
		return (
			<>
				<h4 className="text-left mb-4 mt-1">{LEAD_TEXT}</h4>
				<ul className="text-left">
					<li>
						<strong>Verification Recording</strong>
						<p>
							Before we proceed, may we have your consent to record a short video to reconfirm the
							important points discussed?
						</p>
						<p>
							This recording will serve as an official reference for future clarity and will not be
							shared publicly.
						</p>
					</li>
					<li className="mt-3">
						<strong>Course Entry and Purpose</strong>
						<p>
							Our WSQ courses require at least {specialENStr} qualification or equivalent literacy
							and numeracy skills, and are designed to help learners gain practical, work-relevant
							skills for career and personal growth.
						</p>
						<p>
							You acknowledge this and understand that if you do not meet the MER, you may face some
							learning challenges but you are still keen to proceed with enrolment.
						</p>
					</li>
					<li className="mt-3">
						<strong>Language, Technology Use and Assessment</strong>
						<p>
							Are you aware that lessons will be conducted in English, that you’ll be expected to
							use a computer and smartphone for class activities, and that there will be an
							assessment at the end of the course to evaluate your learning?
						</p>
					</li>
					<li className="mt-3">
						<strong>Acknowledgement</strong>
						<p>
							Do you confirm that all the above points have been clearly explained to you and that
							you are comfortable to proceed with the enrolment?
						</p>
					</li>
				</ul>
			</>
		);
	}

	return (
		<Modal isOpen={isOpen} centered size="lg">
			<ModalHeader>Record Your Video</ModalHeader>
			<ModalBody>
				{error && (
					<Alert color="danger" className="mb-3">
						{error}
					</Alert>
				)}
				{renderQuestions()}

				<hr className="my-4" />
				<div className="w-100 d-flex justify-content-center">
					<video
						ref={videoRef}
						style={{
							width: "100%",
							maxWidth: 540,
							background: "#000",
							borderRadius: 8,
							maxHeight: 420,
							objectFit: "cover",
						}}
						playsInline
						muted
						autoPlay
						controls={false}
					/>
				</div>
			</ModalBody>
			<ModalFooter className="d-flex w-100 justify-content-between">
				<Button outline color="secondary" onClick={handleCancel}>
					Cancel
				</Button>
				<Button color="danger" onClick={stopAndSave} disabled={!recording}>
					Stop &amp; Save
				</Button>
			</ModalFooter>
		</Modal>
	);
}

function RecordingPreviewModal({ isOpen, onUse, onRecapture, b64 }) {
	return (
		<Modal isOpen={isOpen} centered size="lg">
			<ModalHeader>Use Existing Recording?</ModalHeader>
			<ModalBody>
				<p className="mb-3">
					We found a previous recording. Would you like to continue with it, or re-record a new one?
				</p>
				<div className="w-100 d-flex justify-content-center">
					<video
						style={{
							width: "100%",
							maxWidth: 540,
							background: "#000",
							borderRadius: 8,
							maxHeight: 420,
							objectFit: "cover",
						}}
						src={b64 || ""}
						controls
					/>
				</div>
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" outline onClick={onRecapture}>
					Recapture
				</Button>
				<Button color="success" onClick={onUse}>
					Use This Recording &amp; Continue
				</Button>
			</ModalFooter>
		</Modal>
	);
}

function LoginModal(props) {
	const dispatch = useDispatch();
	const [passwordType, setPasswordType] = useState("password");
	const lg = useMediaQuery("(min-width: 992px)");
	const { message } = useSelector((state) => state.message);
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(loginSchema),
	});
	const [showForgetPassword, setShowForgetPassword] = useState(false);
	const { checkoutAnalyticsData } = useCheckout();

	// const handleLogin = () => {};
	const handleLogin = useMutation(
		(data) => {
			dispatch(clearMessage());
			return dispatch(login(data.email, data.password)).then((data) => {
				return data;
			});
		},
		{
			onSuccess: (data) => {
				props.toggle();
				props.onSuccess(data);
				logGTMUser(
					data.user.learnermaster_id ? data.user.learnermaster_id.toString() : "",
					data.user.learnermaster_name ? data.user.learnermaster_name.toString() : "",
					data.user.learnermaster_dob ? data.user.learnermaster_dob : "",
					data.user.learnermaster_nationality ? data.user.learnermaster_nationality : "",
					data.user.learnermaster_gender ? data.user.learnermaster_gender : "",
				);
				GTMCheckoutUserLoginSuccess(checkoutAnalyticsData, data);
			},
		},
	);

	const togglePassword = () => {
		if (passwordType === "password") {
			setPasswordType("text");
			return;
		}
		setPasswordType("password");
	};

	return (
		<>
			<Modal size="xl" className="modal-dialog-centered" {...props}>
				<EmbeddedResetPasswordModal
					isOpen={showForgetPassword}
					onSuccess={(data) => {
						setTimeout(() => {
							toast.success("Password reset successfully 🎉");
						}, 500);
						GTMCheckoutUserForgetPasswordSuccess(checkoutAnalyticsData);
						setShowForgetPassword(false);
					}}
					toggle={() => {
						setShowForgetPassword(!showForgetPassword);
					}}
				/>

				<div
					style={{ position: "absolute", right: 10, top: 10, zIndex: 10000 }}
					onClick={props.toggle}>
					<i className="fa fa-times" aria-hidden="true"></i>
				</div>

				<Row className="mr-0 ml-0">
					{lg && (
						<Col xs={12} lg={6} className="pl-0 pr-0">
							<img src={CheckoutLoginImage} className={styles.login_modal_img} />
						</Col>
					)}

					<Col xs={12} lg={6} className={classNames(styles.login_modal_col)}>
						<div>
							<h2 className={styles.login_modal_header}>Welcome ! Please Sign In to Continue</h2>
							<Form id="login_form" onSubmit={handleSubmit(handleLogin.mutateAsync)}>
								<FormGroup>
									<Label>Email</Label>
									<InputGroup className={"input-group-merge " + (errors.email ? "is-invalid" : "")}>
										<InputGroupAddon addonType="prepend">
											<InputGroupText
												className={"form-control " + (errors.email ? "is-invalid" : "")}>
												<UserIcon />
											</InputGroupText>
										</InputGroupAddon>
										<input
											placeholder="Email"
											type="email"
											className={"form-control " + (errors.email ? "is-invalid" : "")}
											{...register("email")}
										/>
									</InputGroup>
									<div className="invalid-feedback">{errors.email?.message}</div>
								</FormGroup>

								<FormGroup>
									<Label>Password</Label>
									<InputGroup
										className={
											"input-group-merge " + (errors.password ? "needs-validation is-invalid" : "")
										}>
										<InputGroupAddon addonType="prepend">
											<InputGroupText
												className={"form-control " + (errors.password ? "is-invalid" : "")}>
												{/* <i className='ni ni-lock-circle-open' /> */}
												<LockIcon />
											</InputGroupText>
										</InputGroupAddon>
										<input
											placeholder="Password"
											type={passwordType}
											className={"form-control " + (errors.password ? "is-invalid" : "")}
											{...register("password")}
										/>
										<InputGroupAddon addonType="append">
											<InputGroupText onClick={togglePassword}>
												{passwordType === "password" ? (
													<i className="far fa-eye-slash"></i>
												) : (
													<i className="far fa-eye"></i>
												)}
											</InputGroupText>
										</InputGroupAddon>
									</InputGroup>
									<div className="invalid-feedback">{errors.password?.message}</div>
								</FormGroup>

								<div className="custom-control custom-control-alternative custom-checkbox text-center d-none">
									<input className="custom-control-input" id=" customCheckLogin" type="checkbox" />
									<label className="custom-control-label d-none" htmlFor="customCheckLogin">
										<span className="text-muted">Remember me</span>
									</label>
								</div>

								<div className="text-center">
									<Button
										className={classNames("btn-gradient", styles.submit_button)}
										type="submit"
										disabled={handleLogin.isLoading}>
										{handleLogin.isLoading && (
											<span className="spinner-border spinner-border-sm mr-2"></span>
										)}
										<span>Sign in</span>
									</Button>
								</div>
								<div className="mt-4 text-center">
									<a
										href="/login/reset-password"
										target="_blank"
										className="ml-2"
										onClick={(e) => {
											e.preventDefault();
											setShowForgetPassword(true);
											GTMCheckoutUserForgetPasswordButton(checkoutAnalyticsData);
										}}>
										Forgot Password?
									</a>
								</div>
								{message && (
									<div className="form-group pt-3">
										<div className="alert alert-danger" role="alert">
											{message}
										</div>
									</div>
								)}
							</Form>
						</div>
					</Col>
				</Row>
			</Modal>
		</>
	);
}

function SuccessCreateAccountModal(props) {
	return (
		<Modal size="xl" className="modal-dialog-centered" {...props}>
			<div className={styles.successModalContainer}>
				<CreateAccountFloating />
				<h2 className={classNames(styles.successHeading, "mt-5")}>
					<span className={styles.successCongrats}>Congratulations!</span>
					<span>Your account has been created successfully</span>
				</h2>
				<p className={styles.successMessage}>
					<span>
						Your account has been created and you are one step away from acquiring a new skill.
					</span>
				</p>
				<Button className={classNames("btn-gradient")} onClick={props.onNext}>
					Next
				</Button>
			</div>
		</Modal>
	);
}

function PreventCorporateAccountModal(props) {
	const { user } = props;
	const dispatch = useDispatch();
	/**
	 * case 1 : if has invidual account ask them to switch
	 * case 2 : if has don't have indivual account ask them to create one
	 * 	- case 2.1 if there already has a linked account, ask whether to switch to individual account
	 *  - case 2.2 if there is no linked account, ask them to create one
	 */
	const navigate = useNavigate();

	console.log("userdata hhihih", user);
	const hasIndividualAccount =
		user && user.available_account_types && user.available_account_types.includes("individual");

	const handleBack = () => {
		navigate("/");
	};

	const switchAccount = useMutation(async () => {
		await dispatch(switchAccountType("individual"));
		toast.success("Switched to individual account");
		// window.location.reload();
	});

	const handleLogoutClick = () => {
		dispatch(logout());
	};

	return (
		<Modal size="xl" className="modal-dialog-centered" {...props}>
			<Container className="text-center p-4">
				<h2>
					<span className={styles.successCongrats}></span>
					<span>You're currently using a corporate account</span>
				</h2>
				<p className={styles.successMessage}>
					You are <b>not allow to signup </b> course yourself under <b>corporate account</b> .<br />
					Please use individual account to continue.
				</p>
				<Button onClick={handleBack}>
					<i className="fa fa-arrow-left mr-2"></i>
					Return Home
				</Button>
				{hasIndividualAccount ? (
					<Button className={classNames("btn-gradient")} onClick={() => switchAccount.mutate()}>
						{switchAccount.isLoading ? <Spinner size={"md"} /> : "Switch To Individiual Account"}
					</Button>
				) : (
					<Button className={classNames("btn-gradient")} onClick={() => handleLogoutClick()}>
						Logout and Signup as Individual
					</Button>
				)}
			</Container>
		</Modal>
	);
}

function ErrorDisplay({ title, description, children, ...rest }) {
	return (
		<Alert color="danger" {...rest}>
			{title ? <h3 style={{ color: "white" }}>{title}</h3> : null}
			{description ? <p>{description}</p> : null}
			<div>{children}</div>
		</Alert>
	);
}

ErrorDisplay.propsTypes = {
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	children: PropTypes.oneOfType([PropTypes.element]),
};
